import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { GenericCard } from 'components/display/Card/Card';
import { Button } from 'components/ui/Button';
import GenericBadge from 'components/ui/Badge';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { GenericColors } from 'enums/genericColors';
import { Sizes } from 'enums/sizes';
import { NsKey } from 'enums/NsKey';
import { TitleIconKey } from 'enums/titleIconKey';
import { TitleIconFactory } from 'factories/TitleIconFactory';
import { usePanel } from 'hooks/usePanel';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { useQuoteContractualizationData } from '../../store';
import { mapApiStatusToCardState } from '../../types/api';
import { CardState } from '../../types/cardState';
import { cancelQuoteValidationRequest } from '../../api/quoteValidation';
import { RequestValidationPanel } from '../validation/RequestValidationPanel';
import { ValidationFeedbackPanel } from '../validation/ValidationFeedbackPanel';
import {
  formatValidationDate,
  getValidationDisplayState,
  hasValidatorAnswer
} from '../validation/validationFormat';

const K = 'CONTRACTUALIZATION.CARDS';

/**
 * Quote validation card: state of the latest request (the broker only sees that one),
 * what it means for the rest of the contractualization, and the actions the back allows
 * (`peutDemander`, `peutAnnuler`). The color follows `cardStatuses.validation`.
 */
export const ValidationCard: React.FC = () => {
  const { t } = useTranslation(NsKey.Contractualization);
  const { openPanel } = usePanel();
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();
  const quoteData = useQuoteContractualizationData();

  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const devisId = quoteData?.quoteId;
  const validation = quoteData?.validation ?? null;
  const demande = validation?.derniereDemande ?? null;
  const state = getValidationDisplayState(demande);

  const badgeColor = useMemo(() => {
    const status = quoteData?.cardStatuses?.validation?.status;
    if (!status) return GenericColors.Gray;
    switch (mapApiStatusToCardState(status)) {
      case CardState.CRITICAL:
        return GenericColors.Red;
      case CardState.IN_PROGRESS:
        return GenericColors.Yellow;
      case CardState.VALID:
        return GenericColors.Green;
      default:
        return GenericColors.Gray;
    }
  }, [quoteData?.cardStatuses?.validation?.status]);

  const onRequest = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!devisId) return;
      openPanel(
        { title: t(`${K}.VALIDATION_REQUEST_PANEL.TITLE`), size: Sizes.xl },
        <RequestValidationPanel devisId={devisId} onSuccess={refreshSubscriptionProcess} />
      );
    },
    [devisId, openPanel, refreshSubscriptionProcess, t]
  );

  const onSeeFeedback = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!demande) return;
      openPanel(
        { title: t(`${K}.VALIDATION_FEEDBACK_PANEL.TITLE`), size: Sizes['3xl'] },
        <ValidationFeedbackPanel demande={demande} />
      );
    },
    [demande, openPanel, t]
  );

  const handleCancelConfirm = useCallback(async () => {
    if (!devisId) return;
    setIsCancelling(true);
    try {
      const res = await cancelQuoteValidationRequest(devisId);
      if (res.isSuccess) {
        // Success toaster shown automatically from OperationResponse.notification.
        refreshSubscriptionProcess();
      } else {
        toast.error(res.errorMessage || t(`${K}.VALIDATION_CANCEL_CONFIRM.ERROR`));
      }
    } catch (error) {
      console.error('Error cancelling the quote validation request:', error);
      toast.error(t(`${K}.VALIDATION_CANCEL_CONFIRM.ERROR`));
    } finally {
      setIsCancelling(false);
      setIsCancelOpen(false);
    }
  }, [devisId, refreshSubscriptionProcess, t]);

  if (!validation || validation.mode === 'Absente') return null;

  // Names are optional in the DTO: the sentence then drops the "by …" part.
  const withName = (key: string, name: string | null | undefined, date: Date | null | undefined) =>
    name
      ? t(`${K}.VALIDATION_TEXT.${key}_BY`, { name, date: formatValidationDate(date) })
      : t(`${K}.VALIDATION_TEXT.${key}`, { date: formatValidationDate(date) });

  const renderStateText = () => {
    switch (state) {
      case 'PENDING':
        return withName('PENDING', demande?.demandeePar, demande?.dateDemande);
      case 'VALIDATED':
        return withName('VALIDATED', demande?.traiteePar, demande?.dateReponse ?? demande?.dateDemande);
      case 'VALIDATED_WITH_CHANGES':
        return withName('VALIDATED_WITH_CHANGES', demande?.traiteePar, demande?.dateReponse ?? demande?.dateDemande);
      case 'INVALIDATED':
        return t(`${K}.VALIDATION_TEXT.INVALIDATED`, {
          date: formatValidationDate(demande?.dateReponse ?? demande?.dateDemande)
        });
      case 'REFUSED':
        return withName('REFUSED', demande?.traiteePar, demande?.dateReponse ?? demande?.dateDemande);
      default:
        return validation.mode === 'Obligatoire'
          ? t(`${K}.VALIDATION_TEXT.REQUIRED`)
          : t(`${K}.VALIDATION_TEXT.OPTIONAL`);
    }
  };

  // After an invalidation, the broker must know whether he has to ask again.
  const modeHint =
    state === 'INVALIDATED'
      ? validation.mode === 'Obligatoire'
        ? t(`${K}.VALIDATION_TEXT.REQUIRED`)
        : t(`${K}.VALIDATION_TEXT.OPTIONAL`)
      : null;

  const showFeedback = hasValidatorAnswer(demande);
  const feedbackLabel = demande?.devisApres
    ? t(`${K}.VALIDATION_SEE_CHANGES`)
    : t(`${K}.VALIDATION_SEE_FEEDBACK`);

  return (
    <GenericCard className="w-full">
      <GenericCard.Header
        icon={<TitleIconFactory titleIconKey={TitleIconKey.Validation} />}
        titleAside={
          <GenericBadge
            color={badgeColor}
            label={t(`${K}.VALIDATION_STATUS.${state}`)}
            badgeType="status"
          />
        }
      >
        {t(`${K}.VALIDATION_TITLE`)}
      </GenericCard.Header>
      <GenericCard.Body>
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-gray-800">{renderStateText()}</p>
          {modeHint && <p className="text-gray-600">{modeHint}</p>}

          {state === 'PENDING' && demande?.messageCourtier && (
            <div className="rounded-lg border bg-white p-3">
              <p className="text-xs font-medium text-gray-500">{t(`${K}.VALIDATION_TEXT.BROKER_MESSAGE`)}</p>
              <p className="whitespace-pre-line text-gray-700">{demande.messageCourtier}</p>
            </div>
          )}

          {state === 'REFUSED' && demande?.messageValideur && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-medium text-red-700">{t(`${K}.VALIDATION_TEXT.VALIDATOR_MESSAGE`)}</p>
              <p className="whitespace-pre-line text-red-800">{demande.messageValideur}</p>
            </div>
          )}

          {validation.configurationIncomplete && (
            <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-yellow-800">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 flex-shrink-0" />
              <p>{t(`${K}.VALIDATION_TEXT.CONFIG_INCOMPLETE`)}</p>
            </div>
          )}

          {(validation.peutDemander || validation.peutAnnuler || showFeedback) && (
            <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
              {showFeedback && (
                <Button color="white" round onClick={onSeeFeedback}>
                  {feedbackLabel}
                </Button>
              )}
              {validation.peutAnnuler && (
                <Button
                  color="white"
                  round
                  onClick={(e: MouseEvent) => { e.stopPropagation(); setIsCancelOpen(true); }}
                  disabled={isCancelling}
                >
                  {t(`${K}.VALIDATION_CANCEL_BUTTON`)}
                </Button>
              )}
              {validation.peutDemander && (
                <Button color="secondary" round onClick={onRequest} disabled={!devisId}>
                  {t(`${K}.VALIDATION_REQUEST_BUTTON`)}
                </Button>
              )}
            </div>
          )}
        </div>
      </GenericCard.Body>

      <ConfirmationOverlay
        isOpen={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
        onConfirm={handleCancelConfirm}
        title={t(`${K}.VALIDATION_CANCEL_CONFIRM.TITLE`)}
        description={t(`${K}.VALIDATION_CANCEL_CONFIRM.DESCRIPTION`)}
        confirmLabel={t(`${K}.VALIDATION_CANCEL_CONFIRM.CONFIRM`)}
        variant="warning"
        isLoading={isCancelling}
      />
    </GenericCard>
  );
};
